import React from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { buildClassName } from 'utils/build-class-name';
import { searchFilterIds } from 'constants/test-constants';

// Atomic components
import IconButton from 'components/atomic/molecules/icon-button';
import SelectDropdown from 'components/atomic/molecules/select-dropdown';
import { FilterIcon, BellIcon, VerticalSwapIcon } from 'components/atomic/atoms/icons';
import { useIsIphoneSmallOnlyV2 } from 'hooks/use-breakpoint-v2';

// Styles
import styles from './mobile-filter-bar-v2.module.scss';

import type { Sort } from 'contexts/preferences/listing-params/types';

// Legacy component - to be migrated later
const MyLinkMyLeadCreate = dynamic(() => import('components/my-link-my-lead-create'));

//#region Types
export interface SortOption {
  value: Sort;
  label: string;
}

export interface MobileListingFilterBarProps {
  /** Current sort value */
  sort: Sort;
  /** Available sorting options */
  sortingOptions: SortOption[];
  /** Handler for sort changes */
  onSortChanged: (sort: Sort) => void;
  /** Handler for filter button click */
  onMobileFilterButtonClick: () => void;
  /** Whether to show MyLinkMyLeadCreate component */
  showsMyLinkMyLeadCreate: boolean;
  /** Whether any filters are currently set */
  hasFiltersSet: boolean;
  /** Whether to show the select dropdown */
  showSelectDropdown: boolean;
  /** Handler for save search button click */
  onSaveSearchButtonClick: () => void;
  /** Whether to show the save search / Get Alerts button. On hybrid page we don't but on search page we do. */
  showSaveSearchButton?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Handler for analytics on filter button click */
  onFilterButtonClickAnalytics?: () => void;
  /** Handler for analytics on sort change */
  onSortChangedAnalytics?: (sort: Sort) => void;
  /** Handler for analytics on save search button click */
  onSaveSearchButtonClickAnalytics?: () => void;
}
//#endregion

/**
 * MobileListingFilterBarV2 - Mobile filter bar for listing pages
 * 
 * Component Layer: Domain (src/ui)
 * 
 * Features:
 * - Filter button with active state
 * - Sort dropdown with icon
 * - Save search / Get alerts button
 * - MyLinkMyLeadCreate integration
 * 
 * @example
 * ```tsx
 * <MobileListingFilterBarV2
 *   sort={currentSort}
 *   sortingOptions={sortOptions}
 *   onSortChanged={handleSortChange}
 *   onMobileFilterButtonClick={openFilters}
 *   showsMyLinkMyLeadCreate={false}
 *   isMobile={true}
 *   hasFiltersSet={true}
 *   onSaveSearchButtonClick={handleSaveSearch}
 * />
 * ```
 */
export function MobileListingFilterBarV2({
  sort,
  sortingOptions,
  onSortChanged,
  onMobileFilterButtonClick,
  showsMyLinkMyLeadCreate,
  showSelectDropdown,
  hasFiltersSet,
  onSaveSearchButtonClick,
  showSaveSearchButton = true,
  className,
  onFilterButtonClickAnalytics,
  onSortChangedAnalytics,
  onSaveSearchButtonClickAnalytics,
}: MobileListingFilterBarProps) {
  const { t } = useTranslation(Namespaces.COMMON);

  const handleFilterButtonClick = () => {
    onFilterButtonClickAnalytics?.();
    onMobileFilterButtonClick();
  };

  const handleSortChanged = (newSort: Sort) => {
    onSortChangedAnalytics?.(newSort);
    onSortChanged(newSort);
  };

  const handleSaveSearchButtonClick = () => {
    onSaveSearchButtonClickAnalytics?.();
    onSaveSearchButtonClick();
  };

  const isSmallMobile = useIsIphoneSmallOnlyV2();
  const size = isSmallMobile ? 'small' : 'medium';

  return (
    <div className={buildClassName(styles.component, className)}>
      <IconButton
        className={buildClassName(
          styles['mobile-filter-btn'],
          hasFiltersSet && styles['has-filters-set']
        )}
        testId={searchFilterIds.mobileFilterButton}
        leftIcon={<FilterIcon />}
        label={t(LOCALE_COMMON_PATHS.SEARCH_ALL_FILTERS, { defaultValue: 'All Filters' })}
        onClick={handleFilterButtonClick}
        size={size}
      />

      {showSelectDropdown && (
        <SelectDropdown
          className={styles['sort-by']}
          testId={searchFilterIds.sortByDropdown}
          ariaLabel="Sort by dropdown"
          value={sort}
          options={sortingOptions}
          onValueChange={handleSortChanged}
          leftIcon={<VerticalSwapIcon />}
          variant="pill"
          showDropdownIcon={false}
          size={size}
        />
      )}

      {(showSaveSearchButton || showsMyLinkMyLeadCreate) && (
        <div className={styles['right-buttons']}>
          {showsMyLinkMyLeadCreate ? (
            <MyLinkMyLeadCreate />
          ) : (
            <IconButton
              onClick={handleSaveSearchButtonClick}
              leftIcon={<BellIcon />}
              label={t(LOCALE_COMMON_PATHS.SEARCH_GET_ALERTS, { defaultValue: 'Get Alerts' })}
              testId={searchFilterIds.saveSearchButtonMapPage}
              size={size}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default MobileListingFilterBarV2;
